import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { PrintSettingsDialog } from './PrintSettingsDialog';
import { MailingBatchPanel } from './MailingBatchPanel';
import { PrintJobDate } from './PrintJobDate';
import { QueueDateBar } from './QueueDateBar';
import { BatchTimingSummary, fmtDur, jobTiming } from './BatchTimingSummary';
import { Loader2, PenLine, Settings, Download, Eye, RotateCcw, RefreshCw, Trash2, Timer, FileText, QrCode, Mail, CheckCircle2, ChevronDown, FastForward, Printer, AlertTriangle, Repeat } from 'lucide-react';
import { PrioBadge } from './PrioCriteriaPicker';

const STATUS_STYLES = {
  hold: 'text-zinc-300 border-zinc-500/40 bg-zinc-500/10',
  queued: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  sent: 'text-sky-300 border-sky-500/40 bg-sky-500/10',
  writing: 'text-sky-300 border-sky-500/40 bg-sky-500/10',
  printed: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  error: 'text-red-300 border-red-500/40 bg-red-500/10',
};

const machineLabel = (m) => {
  const st = (m.status || '').toLowerCase();
  if (st === 'printing') return (m.prog || 0) > 0 ? `schreibt ${Math.round(m.prog * 100)} %` : 'schreibt';
  if (st === 'paused') return 'pausiert';
  if (st === 'error') return 'Fehler';
  if (st === 'idle') return 'bereit';
  return m.status || 'bereit';
};

const statusInfo = (job, now) => {
  if (job.status === 'printed' && job.est_done_at && new Date(job.est_done_at).getTime() > now) {
    const secs = Math.max(1, Math.round((new Date(job.est_done_at).getTime() - now) / 1000));
    const ms = (job.machine_status || '').toLowerCase();
    if (ms === 'paused' || ms === 'error') {
      return { label: ms === 'paused' ? 'Maschine pausiert' : 'Maschine: Fehler', cls: STATUS_STYLES.error };
    }
    if (typeof job.progress === 'number' && job.progress > 0) {
      return { label: `writing… ${Math.round(job.progress * 100)} %`, cls: STATUS_STYLES.writing };
    }
    const label = secs >= 90 ? `writing… ~${Math.ceil(secs / 60)} min` : `writing… ~${secs} s`;
    return { label, cls: STATUS_STYLES.writing };
  }
  const map = { hold: 'vorbereitet', queued: 'queued', sent: 'sending…', printed: 'written', error: 'error' };
  return { label: map[job.status] || job.status, cls: STATUS_STYLES[job.status] || '' };
};

const DOC_BADGES = {
  letter: ['Brief', FileText],
  flyer: ['Flyer', QrCode],
  envelope: ['Umschlag', Mail],
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export const PrintQueueCard = ({ refreshKey, onLettersChanged }) => {
  const [settings, setSettings] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    api.printSettings().then(r => setSettings(r.data)).catch(() => {});
    api.listPrintJobs().then(r => setJobs(r.data.jobs || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 10000);
    const tick = setInterval(() => setNow(Date.now()), 5000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [load]);

  const downloadScript = async () => {
    try {
      const res = await api.downloadPrintAgentScript();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/x-python' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'olta_print_agent.py'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Agent script downloaded — run it on the PC next to the iAuto machine');
    } catch (err) { toast.error(err.response?.data?.detail || 'Download failed'); }
  };

  const devices = settings?.devices || [];

  const switchDevice = async (id) => {
    try { await api.activatePrintDevice(id); load(); toast.success('Aktives Gerät gewechselt'); }
    catch { toast.error('Fehlgeschlagen'); }
  };

  const reassignDevice = async (job, deviceId) => {
    if (!deviceId || deviceId === job.device_id) return;
    try { await api.reassignPrintJobDevice(job.id, deviceId); load(); toast.success('Job verschoben'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const viewSvg = async (job) => {
    try {
      const res = await api.getPrintJobSvg(job.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'image/svg+xml' }));
      window.open(url, '_blank');
    } catch { toast.error('Could not load SVG'); }
  };

  const retry = async (job) => {
    setBusyId(job.id);
    try { await api.retryPrintJob(job.id); toast.success('Job re-queued'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusyId(''); }
  };

  const reprintIdentical = async (job) => {
    if (!window.confirm(`${job.company_name}: Exakt dieselbe Datei noch einmal als Testblatt schreiben?\n\nBitte ein leeres Blatt einlegen. Das Testblatt wird als Nächstes geschrieben und zählt nicht als versendeter Brief.\n\nVergleich: Ist danach eine ANDERE Zeile kleiner, liegt es an der Maschine — ist es dieselbe Zeile, an der Datei.`)) return;
    setBusyId(job.id);
    try { await api.reprintPrintJobIdentical(job.id); toast.success('Gegenprobe in der Queue — wird als Nächstes geschrieben'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Gegenprobe fehlgeschlagen'); }
    finally { setBusyId(''); }
  };

  const remove = async (job) => {
    const transferred = job.status === 'sent' || (job.status === 'printed' && job.est_done_at && new Date(job.est_done_at).getTime() > now);
    if (transferred && !window.confirm(`${job.company_name}: Dieser Job wurde bereits an die Maschine übertragen.\n\nTrotzdem aus der Queue entfernen? Falls die iAuto gerade schreibt, läuft das Schreiben am Gerät weiter — nur der Eintrag hier verschwindet.`)) return;
    setBusyId(job.id);
    try { await api.deletePrintJob(job.id); load(); }
    catch { toast.error('Failed'); }
    finally { setBusyId(''); }
  };

  const online = settings?.agent_online;
  const openJobs = jobs.filter(j => j.status === 'queued' || j.status === 'hold').length;
  const sentJobs = jobs.filter(j => j.status === 'sent').length;
  const errorJobs = jobs.filter(j => j.status === 'error').length;
  const isDone = (job) => job.status === 'printed' && !(job.est_done_at && new Date(job.est_done_at).getTime() > now);
  const openList = jobs.filter(j => !isDone(j));
  const doneList = jobs.filter(isDone);
  const [doneOpen, setDoneOpen] = useState(() => {
    try { return window.localStorage.getItem('print.doneOpen') === '1'; } catch { return false; }
  });
  const toggleDone = () => setDoneOpen(v => {
    const nv = !v;
    try { window.localStorage.setItem('print.doneOpen', nv ? '1' : '0'); } catch { /* ignore */ }
    return nv;
  });
  const doneCounts = [['letter', 'Briefe'], ['flyer', 'Flyer'], ['envelope', 'Umschläge']]
    .map(([t, label]) => [label, doneList.filter(j => (j.doc_type || 'letter') === t).length])
    .filter(([, n]) => n > 0);

  const renderJob = (job) => {
    const info = statusInfo(job, now);
    const [docLabel0, DocIcon] = DOC_BADGES[job.doc_type || 'letter'] || DOC_BADGES.letter;
    const docLabel = job.letter_kind === 'breakup' ? `Break-up-${docLabel0}` : docLabel0;
    return (
      <div key={job.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`print-job-row-${job.id}`}>
        <span className="text-xs font-medium text-foreground">{job.company_name}</span>
        <PrioBadge prio={job.prio} testid={`print-job-prio-${job.id}`} />
        <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono" data-testid={`print-job-doc-${job.id}`}>
          <DocIcon className="w-3 h-3" /> {docLabel}
        </span>
        {job.adjusted && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-amber-500/40 text-amber-300 bg-amber-500/10 font-mono"
            data-testid={`print-job-adjusted-${job.id}`}
            title="Dieser Brief hat individuell nachjustierte Positionen (weicht vom Standard-Layout ab)">
            angepasst
          </span>
        )}
        <PrintJobDate job={job} onChanged={load} />
        {job.batch_seq > 0 && <span className="text-[10px] text-muted-foreground font-mono">#{job.batch_seq}</span>}
        {job.pages > 1 && <span className="text-[10px] text-muted-foreground font-mono">p. {job.page}/{job.pages}</span>}
        {devices.length > 1 && (
          ['queued', 'hold', 'error'].includes(job.status)
            ? <select value={job.device_id || ''} onChange={e => reassignDevice(job, e.target.value)}
                data-testid={`print-job-device-${job.id}`} title="iAuto-Gerät für diesen Job"
                className="text-[10px] rounded-sm bg-secondary/40 border border-border px-1 py-0.5 text-muted-foreground max-w-[120px] focus:outline-none">
                {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            : <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border/60 text-muted-foreground font-mono"
                data-testid={`print-job-device-${job.id}`}>{job.device_name || '—'}</span>
        )}
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono ${info.cls}`}
          data-testid={`print-job-status-${job.id}`}>
          {info.label}
        </span>
        {job.status === 'queued' && !!job.est_seconds && (
          <span className="text-[10px] text-muted-foreground font-mono">≈ {Math.ceil(job.est_seconds / 60)} min</span>
        )}
        {isDone(job) && (() => {
          const t = jobTiming(job);
          return t ? (
            <span className="flex items-center gap-0.5 text-[10px] text-emerald-300/80 font-mono"
              data-testid={`print-job-time-${job.id}`} title="Maschinenzeit (Übertragung + Schreiben)">
              <Timer className="w-3 h-3" /> {fmtDur(t.secs)}
            </span>
          ) : null;
        })()}
          {job.status === 'error' && job.error && (
            <span className={`text-[10px] truncate max-w-[280px] ${job.stale_sent ? 'text-amber-300' : 'text-red-400'}`} title={job.error}
              data-testid={`print-job-error-${job.id}`}>{job.stale_sent ? 'hängt — keine Rückmeldung des Agents (30 Min.)' : job.error}</span>
          )}
          <span className="ml-auto flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] text-muted-foreground">{fmtTime(job.created_at)}</span>
            <button onClick={() => viewSvg(job)} data-testid={`print-job-view-${job.id}`}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors" title="View handwriting SVG">
              <Eye className="w-3.5 h-3.5" />
            </button>
            {isDone(job) && (
              <button onClick={() => reprintIdentical(job)} disabled={busyId === job.id} data-testid={`print-job-reprint-${job.id}`}
                className="p-1 text-muted-foreground hover:text-sky-300 transition-colors"
                title="Gegenprobe: exakt dieselbe Datei nochmal als Testblatt schreiben">
                <Repeat className="w-3.5 h-3.5" />
              </button>
            )}
            {(job.status === 'error' || job.status === 'sent') && (
              <button onClick={() => retry(job)} disabled={busyId === job.id} data-testid={`print-job-retry-${job.id}`}
                className="p-1 text-amber-400 hover:text-amber-300 transition-colors" title="Retry">
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => remove(job)} disabled={busyId === job.id} data-testid={`print-job-delete-${job.id}`}
              className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
              title={job.status === 'sent' ? 'Aus der Queue entfernen (bereits übertragen — mit Rückfrage)' : 'Aus der Queue entfernen'}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </span>
      </div>
    );
  };

  const retryAllErrors = async () => {
    try {
      const r = await api.retryAllPrintErrors();
      toast.success(`${r.data.requeued} fehlgeschlagene Jobs wieder in die Queue gestellt`);
      load();
    } catch { toast.error('Fehlgeschlagen'); }
  };

  const clearQueue = async () => {
    let includeSent = false;
    if (openJobs > 0 && !window.confirm(`${openJobs} wartende Jobs stornieren?\n\nBereits an die Maschine übertragene und geschriebene Jobs bleiben erhalten.`)) return;
    if (sentJobs > 0) {
      includeSent = window.confirm(`Zusätzlich ${sentJobs} bereits übertragene Job${sentJobs !== 1 ? 's' : ''} („sending…“) entfernen?\n\nOK = auch diese entfernen (sie hängen vermutlich — die Maschine schreibt ggf. trotzdem weiter).\nAbbrechen = nur die wartenden Jobs stornieren.`);
      if (openJobs === 0 && !includeSent) return;
    }
    try {
      const r = await api.clearPrintQueue(includeSent);
      toast.success(`${r.data.deleted} Jobs storniert`);
      load();
    } catch { toast.error('Fehlgeschlagen'); }
  };

  // Both iAutos write in parallel (Talu, 18.10.2026): remaining time per device, total = slowest device
  const etaByDevice = jobs.reduce((acc, j) => {
    let secs = 0;
    if (j.status === 'queued' || j.status === 'sent') secs = j.est_seconds || 90;
    else if (j.status === 'printed' && j.est_done_at) secs = Math.max(0, (new Date(j.est_done_at).getTime() - now) / 1000);
    if (!secs) return acc;
    const key = j.device_id || j.device_name || 'iAuto';
    acc[key] = { name: j.device_name || 'iAuto', secs: (acc[key]?.secs || 0) + secs };
    return acc;
  }, {});
  const etaDevices = Object.values(etaByDevice).filter(d => d.secs > 0);
  const etaSeconds = etaDevices.length ? Math.max(...etaDevices.map(d => d.secs)) : 0;
  const fmtEta = (secs) => (secs >= 90 ? `~${Math.ceil(secs / 60)} min` : `~${Math.round(secs)} s`);
  const etaLabel = etaSeconds > 0 ? fmtEta(etaSeconds) : '';
  const etaPerDevice = etaDevices.length > 1 ? etaDevices.map(d => `${d.name} ${fmtEta(d.secs)}`).join(' · ') : '';
  const waiting = jobs.some(j => j.status === 'printed' && j.est_done_at && new Date(j.est_done_at).getTime() > now);

  const skipWait = async () => {
    try {
      const r = await api.skipPrintWait();
      toast.success(r.data.learned_speed
        ? `Weiter! Stifttempo gelernt: ${r.data.learned_speed} mm/s — künftige Wartezeiten werden automatisch kürzer`
        : 'Weiter — der nächste Job geht sofort an die Maschine');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="print-queue-card">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
        <PenLine className="w-4 h-4 text-purple-400" />
        <p className="font-heading text-base text-foreground">iAuto handwriting machine</p>
        <span className={`flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-full border ${online ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' : 'text-red-300 border-red-500/40 bg-red-500/10'}`}
          data-testid="print-agent-status">
          <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-400' : 'bg-red-400'}`} />
          {online ? 'Agent online' : 'Agent offline'}
        </span>
        {devices.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="print-active-device">
            <Printer className="w-3.5 h-3.5 text-muted-foreground" />
            <select value={settings?.active_device_id || ''} onChange={e => switchDevice(e.target.value)}
              data-testid="print-active-device-select" title="Aktives iAuto-Gerät — neue Jobs gehen hierhin"
              className="h-7 text-[11px] rounded-sm bg-secondary/40 border border-border px-2 text-foreground focus:outline-none">
              {devices.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.online ? ' • online' : ' • offline'}{d.queued ? ` (${d.queued})` : ''}
                  {d.machine_state?.machines?.[0] ? ` · ${machineLabel(d.machine_state.machines[0])}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {etaLabel && (
          <span className="flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border text-sky-300 border-sky-500/40 bg-sky-500/10"
            data-testid="print-queue-eta" title={etaPerDevice ? `Beide Maschinen schreiben parallel — ${etaPerDevice}` : 'Geschätzte Restzeit der Warteschlange'}>
            <Timer className="w-3 h-3" /> {etaLabel} until queue is done
            {etaPerDevice && <span className="text-sky-300/70" data-testid="print-queue-eta-devices">({etaPerDevice})</span>}
          </span>
        )}
        {waiting && (
          <Button size="sm" onClick={skipWait} data-testid="print-skip-wait-btn"
            title="Die Wartezeit ist nur eine Schätzung — wenn die Maschine schon fertig ist, hier klicken: der nächste Job startet sofort und das System lernt das echte Tempo"
            className="h-7 text-[11px] rounded-sm bg-emerald-700 hover:bg-emerald-600 text-white px-2.5">
            <FastForward className="w-3.5 h-3.5" /> Maschine ist fertig — weiter
          </Button>
        )}
        {settings && (
          <span className="text-[10px] text-muted-foreground font-mono" data-testid="print-font-name">
            Font: {settings.font_name}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {errorJobs > 0 && (
            <Button size="sm" variant="outline" onClick={retryAllErrors} data-testid="print-retry-errors-btn"
              className="h-8 text-xs rounded-sm border-amber-500/40 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10">
              <RefreshCw className="w-3.5 h-3.5" /> Fehler erneut senden ({errorJobs})
            </Button>
          )}
          {(openJobs > 0 || sentJobs > 0) && (
            <Button size="sm" variant="outline" onClick={clearQueue} data-testid="print-clear-queue-btn"
              className="h-8 text-xs rounded-sm border-red-500/40 text-red-400 hover:text-red-300 hover:bg-red-500/10">
              <Trash2 className="w-3.5 h-3.5" /> Queue leeren ({openJobs + sentJobs})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={downloadScript} data-testid="print-download-agent-btn"
            className="h-8 text-xs rounded-sm border-border">
            <Download className="w-3.5 h-3.5" /> Agent script
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowSettings(true)} data-testid="print-settings-btn"
            className="h-8 text-xs rounded-sm border-border">
            <Settings className="w-3.5 h-3.5" /> Settings
          </Button>
        </span>
      </div>

      {!online && (
        <p className="px-4 py-2 text-[11px] text-muted-foreground border-b border-border" data-testid="print-agent-hint">
          Run <span className="font-mono text-foreground">python olta_print_agent.py</span> (Windows) or the <span className="font-mono text-foreground">.command</span> launcher (Mac — Settings → Geräte)
          on the computer next to the iAuto machine. It fetches queued letters and sends them to the machine automatically.
        </p>
      )}

      {devices.filter(d => !d.host_id && d.login_required).map(d => (
        <div key={`login-${d.id}`} className="px-4 py-2.5 border-b border-red-500/40 bg-red-500/10 text-[11px] text-red-200 flex items-start gap-2"
          data-testid={`print-login-required-${d.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-400" />
          <span>
            <span className="font-medium">{d.name}: UUNA-TEK-Programm abgemeldet</span>
            {` seit ${new Date(d.login_required.at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
            {d.login_required.company_name ? ` (bei „${d.login_required.company_name}“)` : ''}
            {' — bitte im iAuto-Programm auf diesem Computer neu einloggen. Die Briefe warten in der Queue und laufen danach automatisch in der richtigen Reihenfolge weiter.'}
          </span>
        </div>
      ))}

      {devices.filter(d => d.machine_wait).map(d => (
        <div key={`wait-${d.id}`} className="px-4 py-2.5 border-b border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-100 flex items-start gap-2"
          data-testid={`print-machine-wait-${d.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
          <span>
            <span className="font-medium">
              {d.name}: {d.machine_wait.reason === 'upload_stalled' ? 'Upload zur Maschine hängt (WLAN)'
                : d.machine_wait.reason === 'software_hung' ? 'UUNA-TEK-Programm hängt im Upload-Fenster' : 'Maschine nicht verbunden'}
            </span>
            {` seit ${new Date(d.machine_wait.at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
            {d.machine_wait.company_name ? ` (bei „${d.machine_wait.company_name}“)` : ''}
            {d.machine_wait.reason === 'machine_missing'
              ? ' — der Agent wartet und schreibt automatisch weiter, sobald die Maschine in der UUNA-TEK-Software wieder da ist (WLAN/USB prüfen).'
              : ' — der Agent wartet statt Briefe auf Fehler zu setzen. Im UUNA-TEK-Programm das Upload-Fenster schließen bzw. die Maschine neu verbinden; nachts ist USB die sichere Verbindung.'}
            {d.machine_wait.msg ? <span className="block text-[10px] text-amber-200/70 font-mono mt-0.5">{d.machine_wait.msg}</span> : null}
          </span>
        </div>
      ))}

      {devices.filter(d => d.online && !d.host_id && d.api_state && d.api_state.state !== 'ok').map(d => (
        <div key={d.id} className="px-4 py-2 border-b border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-200 flex items-start gap-2"
          data-testid={`print-api-down-${d.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
          <span>
            <span className="font-medium">{d.name}: Agent läuft, aber die UUNA TEK-API antwortet nicht</span>
            {d.api_state.state === 'wrong' ? ' (unter der Adresse antwortet ein anderer Server)' : d.api_state.state === 'none' ? ' (keine iAuto-Adresse hinterlegt)' : ' (nichts erreichbar)'}
            {' — Briefe bleiben in der Queue und starten automatisch, sobald die API antwortet. '}
            <button onClick={() => setShowSettings(true)} className="underline hover:text-amber-100" data-testid={`print-api-down-settings-${d.id}`}>Settings → Geräte → Agent-Log</button>
          </span>
        </div>
      ))}

      <QueueDateBar jobs={jobs} onChanged={load} />

      <MailingBatchPanel jobs={jobs} onChanged={load} onMarkedSent={onLettersChanged} devices={devices} activeDeviceId={settings?.active_device_id || ''} />

      <BatchTimingSummary jobs={jobs} now={now} />

      {jobs.length === 0 ? (
        <p className="px-4 py-6 text-xs text-muted-foreground" data-testid="print-queue-empty">
          No print jobs yet — press “Write letter” on an approved letter to queue it for the machine.
        </p>
      ) : (
        <>
          {openList.length === 0 ? (
            <p className="px-4 py-4 text-xs text-muted-foreground" data-testid="print-queue-all-done">
              Keine offenen Jobs — alles geschrieben ✓
            </p>
          ) : (
            <div className="divide-y divide-border max-h-[60vh] overflow-y-auto" data-testid="print-queue-open-list">
              {openList.map(renderJob)}
            </div>
          )}
          {doneList.length > 0 && (
            <div className="border-t border-border">
              <button onClick={toggleDone} data-testid="print-queue-done-toggle"
                className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-secondary/30 transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-foreground font-medium">Fertig geschrieben</span>
                <span className="text-[10px] text-muted-foreground font-mono" data-testid="print-queue-done-count">
                  {doneCounts.map(([label, n]) => `${n} ${label}`).join(' · ') || doneList.length}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground/60 ml-auto transition-transform ${doneOpen ? '' : '-rotate-90'}`} />
              </button>
              {doneOpen && (
                <div className="divide-y divide-border max-h-[40vh] overflow-y-auto border-t border-border" data-testid="print-queue-done-list">
                  {doneList.map(renderJob)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <PrintSettingsDialog open={showSettings} onOpenChange={setShowSettings} settings={settings} onSaved={load} />
    </div>
  );
};

export default PrintQueueCard;
